import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";

export default function Careers() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    education: "",
    details: "",
  });
  const [file, setFile] = useState(null);
  const [msg, setMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append("cv", file);

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/careers/apply`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.status === "ok") {
        setMsg("✅ Application submitted successfully!");
        setForm({ name: "", email: "", phone: "", education: "", details: "" });
        setFile(null);
      } else {
        setMsg(`❌ ${res.data.message || "Upload failed. Please try again."}`);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || "Upload failed. Please try again.";
      setMsg(`❌ ${errorMsg}`);
      console.error("CV upload error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClasses = "w-full bg-slate-900/50 border border-white/10 text-white rounded-xl px-4 py-3 outline-none focus:border-aseBlue focus:ring-1 focus:ring-aseBlue transition-all duration-300 placeholder-gray-500";
  const labelClasses = "block text-gray-400 text-sm font-medium mb-2 uppercase tracking-wide";

  return (
    <main className="min-h-screen bg-slate-900 text-white pt-32 pb-20 relative overflow-hidden selection:bg-aseBlue selection:text-white">
      {/* Background glow */}
      <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-aseBlue/10 rounded-full blur-[150px] pointer-events-none"></div>

      <div className="max-w-3xl mx-auto px-4 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-display font-bold tracking-tight mb-4">
            Join Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-aseBlue to-purple-400">Mission.</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            We are always looking for exceptional talent to help us build the future of software engineering.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <form
            onSubmit={submit}
            className="bg-slate-800/40 backdrop-blur-xl rounded-3xl p-8 md:p-10 shadow-2xl border border-white/5"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className={labelClasses}>Full Name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Doe" className={inputClasses} />
              </div>
              <div>
                <label className={labelClasses}>Email Address</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="john@example.com" className={inputClasses} />
              </div>
              <div>
                <label className={labelClasses}>Phone Number</label>
                <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+92 300 0000000" className={inputClasses} />
              </div>
              <div>
                <label className={labelClasses}>Highest Education</label>
                <input required value={form.education} onChange={(e) => setForm({ ...form, education: e.target.value })} placeholder="BS Computer Science" className={inputClasses} />
              </div>
            </div>

            <div className="mb-6">
              <label className={labelClasses}>Additional Information</label>
              <textarea
                placeholder="Tell us about your experience, projects, or why you want to join us..."
                value={form.details}
                onChange={(e) => setForm({ ...form, details: e.target.value })}
                className={`${inputClasses} min-h-[120px] resize-y`}
              />
            </div>

            <div className="mb-8">
              <label className={labelClasses}>Upload Resume / CV</label>
              <div className="relative">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setFile(e.target.files?.[0])}
                  className="w-full text-gray-400 file:mr-4 file:px-6 file:py-3 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-white/5 file:text-white hover:file:bg-white/10 file:transition-colors file:cursor-pointer border border-white/10 rounded-xl p-2 bg-slate-900/50"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">Accepted formats: PDF, DOC, DOCX. Max size: 5MB.</p>
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-aseBlue hover:bg-blue-600 text-white font-semibold px-6 py-4 rounded-xl transition-colors duration-300 shadow-[0_0_20px_rgba(0,112,243,0.3)] hover:shadow-[0_0_30px_rgba(0,112,243,0.5)] disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center"
            >
              {isSubmitting ? (
                <span className="animate-pulse">Submitting Application...</span>
              ) : (
                "Submit Application"
              )}
            </motion.button>

            {msg && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mt-6 p-4 rounded-xl border text-center font-medium ${
                  msg.includes("✅") ? "bg-green-500/10 border-green-500/20 text-green-400" : "bg-red-500/10 border-red-500/20 text-red-400"
                }`}
              >
                {msg}
              </motion.div>
            )}
          </form>
        </motion.div>
      </div>
    </main>
  );
}